import { ASSIGNABLE_EMPLOYEE_ROLES } from '@noor/eats-api';
import type { UserRole, UserTab } from '../model/schema';

/**
 * Which tab a role appears under — the single mapping the tab filter and the
 * counts derive from.
 *
 * `unknown` buckets under `admins` so a role the panel doesn't recognise is
 * still reachable somewhere rather than vanishing from every tab.
 */
export const ROLE_TAB: Record<UserRole, UserTab> = {
  'merchant-admin': 'admins',
  manager: 'managers',
  cashier: 'cashiers',
  unknown: 'admins',
};

/**
 * The tab order shown across the top of the desktop table.
 *
 * `all` leads and is deliberately NOT in `ROLE_TAB`: no role maps to it,
 * because it is every role at once. Its count is the roster's own length
 * rather than a sum of the others, so a role the panel does not recognise
 * still shows up in the total.
 */
export const USER_TABS: readonly UserTab[] = [
  'all',
  'admins',
  'managers',
  'cashiers',
] as const;

/**
 * Roles offered in the create form's role picker.
 *
 * `merchant-admin` is NOT among them, and that is a backend rule rather than a
 * product choice: `v1NewIdentity` states that the merchant contour cannot
 * create a merchant-admin (the first admin is minted with the brand itself, on
 * an admin-only route), so offering it would surface an option every submit
 * rejects. Re-exported from `@noor/eats-api` so the list is defined once.
 */
export const ASSIGNABLE_ROLES: readonly UserRole[] = ASSIGNABLE_EMPLOYEE_ROLES;

/** The i18n key (under the `eats` namespace) for a role's display label. */
export function roleLabelKey(role: UserRole): string {
  return `users.roles.${role}`;
}
